import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Brush,
  ResponsiveContainer, ReferenceArea,
} from 'recharts';
import { format } from 'date-fns';
import { exportChartAsPng, DownloadIcon } from '../utils/exportChart';

// Perceptually distinct palette — first colors are maximally different so
// small projects (2–4 cells) get high-contrast pairs automatically.
const COLORS = [
  '#3E68B2', // 1  emew blue
  '#e67e22', // 2  orange       — very distinct from blue
  '#38D430', // 3  green
  '#e74c3c', // 4  red
  '#9370db', // 5  purple
  '#20b2aa', // 6  teal
  '#f39c12', // 7  amber
  '#1abc9c', // 8  emerald
  '#ff7f50', // 9  coral
  '#3498db', // 10 sky blue
  '#c0392b', // 11 dark red
  '#27ae60', // 12 dark green
  '#8e44ad', // 13 violet
  '#16a085', // 14 dark teal
  '#d35400', // 15 dark orange
];

function makeFormatTick(dateFrom, dateTo) {
  const days = (dateFrom && dateTo)
    ? (new Date(dateTo) - new Date(dateFrom)) / 86400000
    : 0;
  if (days > 90) return val => { try { return format(new Date(val), "d MMM ''yy"); } catch { return val; } };
  if (days > 7)  return val => { try { return format(new Date(val), 'd MMM');      } catch { return val; } };
  return         val => { try { return format(new Date(val), 'MM/dd HH:mm');       } catch { return val; } };
}

function makeFormatLabel(dateFrom, dateTo) {
  const days = (dateFrom && dateTo)
    ? (new Date(dateTo) - new Date(dateFrom)) / 86400000
    : 0;
  const fmt = days > 90 ? "MMM d yyyy, HH:mm" : "MMM d, HH:mm";
  return label => label ? format(new Date(label), fmt) : '';
}

// Tooltip shows only the cell whose line is nearest to the cursor's Y position.
// coordinate.y  — cursor Y in SVG pixels (from recharts)
// viewBox       — {x, y, width, height} of the plot area (from recharts)
// We map cursor Y → data value using the visible payload min/max, then pick the closest cell.
function NearestCellTooltip({ active, payload, label, coordinate, viewBox, unit, formatLabel, yDomain }) {
  if (!active || !payload?.length || !coordinate || !viewBox) return null;

  const decimals = unit === 'mΩ' ? 3 : 4;

  const values = payload.map(p => p.value).filter(v => v != null && isFinite(v));
  if (!values.length) return null;

  // Map cursor pixel → data value using the axis's ACTUAL domain. The axis is
  // pinned (see yDomain), so deriving the range from the payload here would put
  // the cursor on the wrong value and highlight the wrong line.
  let top, bottom;
  if (Array.isArray(yDomain) && isFinite(yDomain[0]) && isFinite(yDomain[1])) {
    [bottom, top] = yDomain;
  } else {
    const yMin = Math.min(...values), yMax = Math.max(...values);
    const pad = (yMax - yMin) * 0.05;
    bottom = yMin - pad; top = yMax + pad;
  }
  const span = (top - bottom) || 1;

  const relY = coordinate.y - viewBox.y;             // pixels from top of plot
  const cursorValue = top - (relY / viewBox.height) * span;

  // Pick the payload entry whose value is closest to cursorValue
  const nearest = payload.reduce((best, p) => {
    if (p.value == null || !isFinite(p.value)) return best;
    if (!best) return p;
    return Math.abs(p.value - cursorValue) < Math.abs(best.value - cursorValue) ? p : best;
  }, null);

  if (!nearest) return null;

  return (
    <div style={{
      background: 'white',
      border: '1px solid #dce3ec',
      borderRadius: 6,
      padding: '8px 12px',
      fontSize: 12,
      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      pointerEvents: 'none',
    }}>
      <div style={{ fontWeight: 700, color: '#142D55', marginBottom: 4, fontSize: 11 }}>
        {formatLabel ? formatLabel(label) : (label ? format(new Date(label), 'MMM d, HH:mm') : '')}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: nearest.color, flexShrink: 0 }} />
        <span style={{ fontWeight: 700, color: '#142D55' }}>{nearest.name || nearest.dataKey}</span>
        <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#2c3e50' }}>
          {nearest.value.toFixed(decimals)} {unit}
        </span>
      </div>
    </div>
  );
}

// Key for the synthetic mean series. Underscore-prefixed so it can never collide
// with a real column name coming from the API.
const AVG_KEY  = '__segment_avg';
const AVG_NAME = 'Segment avg';
const AVG_COLOR = '#142D55';   // emew dark navy — outside the per-cell palette
// On a small system every cell is already easy to follow, and the mean sits so
// close to them that it mostly adds clutter — so it starts hidden there. Still
// listed in the legend, so it is one click away.
const AVG_DEFAULT_OFF_MAX = 4;

export default function CellVoltageChart({ rows, columns, title, onBrushChange, unit = 'V', dateFrom, dateTo }) {
  const [hidden, setHidden] = useState(
    () => (columns.length <= AVG_DEFAULT_OFF_MAX ? new Set([AVG_KEY]) : new Set()));
  const brushTimer = useRef(null);
  const cardRef    = useRef(null);
  const formatTick  = makeFormatTick(dateFrom, dateTo);
  const formatLabel = makeFormatLabel(dateFrom, dateTo);

  // Reset visibility when the chart is pointed at a different set of cells
  // (switching project or segment); keyed on the names, not the array identity,
  // so a re-render with an equal list does not wipe the user's choices.
  const columnKey = columns.join(',');
  useEffect(() => {
    setHidden(columns.length <= AVG_DEFAULT_OFF_MAX ? new Set([AVG_KEY]) : new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnKey]);

  // Mean across the whole segment at each timestamp — a stable reference line to
  // judge individual cells against. Deliberately the average of ALL the segment's
  // cells, not just the visible ones, so isolating a cell still compares it with
  // the segment rather than with itself. Nulls are skipped, so the mean tracks
  // whichever cells are reporting; with a single cell it would duplicate that
  // cell's own line, so it is omitted.
  const showAvg = columns.length >= 2;
  const data = useMemo(() => {
    if (!showAvg) return rows;
    return rows.map(row => {
      let sum = 0, n = 0;
      for (const c of columns) {
        const v = row[c];
        if (v != null && isFinite(v)) { sum += v; n++; }
      }
      return n ? { ...row, [AVG_KEY]: sum / n } : row;
    });
  }, [rows, columns, showAvg]);

  // Y axis range, fixed across the whole segment. Recharts would otherwise
  // rescale to whatever is currently visible, so isolating a cell changed the
  // axis under you and made the same trace look different from one click to the
  // next — impossible to compare cells by eye. Derived from every cell in the
  // segment (the mean always lies inside that range), so the scale is identical
  // whether one cell or all of them are shown.
  const yDomain = useMemo(() => {
    let lo = Infinity, hi = -Infinity;
    for (const row of rows) {
      for (const c of columns) {
        const v = row[c];
        if (v != null && isFinite(v)) { if (v < lo) lo = v; if (v > hi) hi = v; }
      }
    }
    if (!isFinite(lo) || !isFinite(hi)) return ['auto', 'auto'];
    // Match the ~5% breathing room recharts adds by default; guard the flat case.
    const pad = (hi - lo) * 0.05 || Math.abs(hi) * 0.05 || 0.1;
    return [lo - pad, hi + pad];
  }, [rows, columns]);

  // Find contiguous periods where all visible cells are off (rectifier off).
  // Resistance mode: cell is off when null.
  // Voltage mode: cell is off when null or below 0.5 V (powered-down rectifier).
  const offPeriods = useMemo(() => {
    if (!rows.length || !columns.length) return [];
    const visible = columns.filter(c => !hidden.has(c));
    if (!visible.length) return [];
    const isVoltage = unit !== 'mΩ';
    const periods = [];
    let start = null;
    for (const row of rows) {
      const isOff = isVoltage
        ? visible.every(c => row[c] == null || Math.abs(row[c]) < 0.5)
        : visible.every(c => row[c] == null);
      if (isOff && start === null) { start = row.t; }
      else if (!isOff && start !== null) { periods.push({ x1: start, x2: row.t }); start = null; }
    }
    if (start !== null) periods.push({ x1: start, x2: rows[rows.length - 1].t });
    return periods;
  }, [rows, columns, hidden, unit]);

  const toggle = (col, e) => {
    if (e.shiftKey) {
      const next = new Set(hidden);
      if (next.has(col)) next.delete(col); else next.add(col);
      setHidden(next);
    } else {
      const allOthersHidden = columns.filter(c => c !== col).every(c => hidden.has(c));
      const next = allOthersHidden ? new Set() : new Set(columns.filter(c => c !== col));
      // Isolating a cell must not silently re-show the segment average if the
      // user had hidden it — that choice is independent of which cells are shown.
      if (hidden.has(AVG_KEY)) next.add(AVG_KEY);
      setHidden(next);
    }
  };

  if (!rows || rows.length === 0) {
    return (
      <div className="card">
        <div className="card-header"><span className="card-title">{title}</span></div>
        <div className="empty-state" style={{ minHeight: 180 }}>
          <div className="empty-state-icon">📊</div>
          <div className="empty-state-text">No data loaded — upload and select files above</div>
        </div>
      </div>
    );
  }

  return (
    <div className="card" ref={cardRef}>
      <div className="card-header">
        <span className="card-title">{title}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={() => exportChartAsPng(cardRef.current, title, dateFrom, dateTo)}
            title="Export as PNG"
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              background: 'none', border: '1px solid #dce3ec',
              borderRadius: 5, padding: '3px 8px', cursor: 'pointer',
              color: '#6b7a8d', fontSize: 11, fontWeight: 500,
            }}
          >
            <DownloadIcon size={12} />
            PNG
          </button>
          <span className="badge badge-grey">{rows.length.toLocaleString()} pts</span>
        </div>
      </div>

      <div className="chart-legend">
        {columns.map((col, i) => (
          <div
            key={col}
            className={`legend-item${hidden.has(col) ? ' hidden' : ''}`}
            onClick={e => toggle(col, e)}
            title="Click to isolate · Shift+click to toggle"
          >
            <span className="legend-dot" style={{ background: COLORS[i % COLORS.length] }} />
            {col}
          </div>
        ))}
        {showAvg && (
          <div
            className={`legend-item${hidden.has(AVG_KEY) ? ' hidden' : ''}`}
            onClick={() => {
              const next = new Set(hidden);
              if (next.has(AVG_KEY)) next.delete(AVG_KEY); else next.add(AVG_KEY);
              setHidden(next);
            }}
            title={`Mean of all ${columns.length} cells in this segment · click to show/hide`}
            style={{ fontWeight: 600 }}
          >
            {/* Dashed swatch instead of a dot, matching the line style */}
            <span
              className="legend-dot"
              style={{
                background: 'none',
                borderTop: `3px dashed ${AVG_COLOR}`,
                borderRadius: 0,
                width: 14,
                height: 0,
                alignSelf: 'center',
              }}
            />
            {AVG_NAME}
          </div>
        )}
        {hidden.size > 0 && (
          <div
            className="legend-item"
            onClick={() => setHidden(new Set())}
            style={{ color: '#3E68B2', fontWeight: 600, marginLeft: 4 }}
          >
            Show all
          </div>
        )}
      </div>

      <div className="chart-container">
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={data} margin={{ top: 4, right: 24, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f5" />
            <XAxis
              dataKey="t"
              tickFormatter={formatTick}
              tick={{ fontSize: 10, fill: '#6b7a8d' }}
              tickLine={false}
              axisLine={{ stroke: '#dce3ec' }}
              interval="preserveStartEnd"
              minTickGap={80}
            />
            <YAxis
              domain={yDomain}
              allowDataOverflow={false}
              tick={{ fontSize: 10, fill: '#6b7a8d' }}
              tickLine={false}
              axisLine={false}
              width={unit === 'mΩ' ? 56 : 48}
              tickFormatter={v => unit === 'mΩ' ? v.toFixed(2) : v.toFixed(3)}
            />
            {/* Shade rectifier-off periods in resistance mode */}
            {offPeriods.map((p, i) => (
              <ReferenceArea
                key={i}
                x1={p.x1} x2={p.x2}
                fill="#b0bec5"
                fillOpacity={0.18}
                stroke="#90a4ae"
                strokeOpacity={0.35}
                strokeDasharray="4 3"
                label={i === 0 ? { value: 'Rectifier off', position: 'insideTop', fontSize: 10, fill: '#8892a0' } : undefined}
              />
            ))}
            <Tooltip
              content={(props) => <NearestCellTooltip {...props} unit={unit} formatLabel={formatLabel} yDomain={yDomain} />}
              isAnimationActive={false}
            />
            {columns.map((col, i) => (
              <Line
                key={col}
                type="monotone"
                dataKey={col}
                stroke={COLORS[i % COLORS.length]}
                dot={false}
                strokeWidth={1.5}
                hide={hidden.has(col)}
                isAnimationActive={false}
                connectNulls={false}
              />
            ))}
            {/* Declared last so it renders above the cell lines */}
            {showAvg && (
              <Line
                type="monotone"
                dataKey={AVG_KEY}
                name={AVG_NAME}
                stroke={AVG_COLOR}
                dot={false}
                strokeWidth={2.75}
                strokeDasharray="7 4"
                hide={hidden.has(AVG_KEY)}
                isAnimationActive={false}
                connectNulls={false}
              />
            )}
            {onBrushChange && (
              <Brush
                dataKey="t"
                height={22}
                stroke="#3E68B2"
                fill="#f4f6fb"
                travellerWidth={6}
                tickFormatter={formatTick}
                onChange={({ startIndex, endIndex }) => {
                  clearTimeout(brushTimer.current);
                  brushTimer.current = setTimeout(() => {
                    const s = rows[startIndex]?.t;
                    const e = rows[endIndex]?.t;
                    if (s && e && s !== e) onBrushChange(s, e);
                  }, 600);
                }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
